import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Snackbar,
  Divider,
  Checkbox,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Collapse,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GroupAdd as GroupAddIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
  ArrowForward as ArrowForwardIcon,
  FilterList as FilterListIcon,
  FilterAlt as FilterAltIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import ExportButton from '../components/ExportButton';
import { exportToExcel, exportToPDF, generateAttestationPDF } from '../services/exportService';
import axios from 'axios';
import { DropzoneArea } from 'mui-file-dropzone';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const BASE_URL = API_URL.replace('/api', '');

// Fonction utilitaire pour extraire un ID, même s'il est imbriqué dans un objet
const extractId = (value) => {
  if (!value) return '';

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }

  if (typeof value === 'object') {
    return String(
      value._id ||
      value.id ||
      value.annee_id ||
      value.filiere_id ||
      value.niveau_id ||
      ''
    ).trim();
  }

  return '';
};

// Fonction utilitaire pour comparer les IDs de manière sécurisée
const compareIds = (id1, id2) => {
  const left = extractId(id1);
  const right = extractId(id2);
  return Boolean(left && right && left === right);
};

// Fonction utilitaire pour trouver un élément par ID (cherche dans plusieurs champs possibles)
const findById = (collection, id) => {
  if (!id || !collection) return null;
  return collection.find(item => 
    compareIds(item._id, id) || 
    compareIds(item.id, id) ||
    compareIds(item._normalizedId, id) ||
    (item.annee_id && compareIds(item.annee_id, id)) ||
    (item.filiere_id && compareIds(item.filiere_id, id)) ||
    (item.niveau_id && compareIds(item.niveau_id, id))
  );
};

// Fonction utilitaire pour normaliser les IDs des entités
const normalizeIds = (entities) => {
  return entities.map(entity => ({
    ...entity,
    _normalizedId: extractId(entity),
    _normalizedAnneeId: extractId(entity.annee_id),
    _normalizedFiliereId: extractId(entity.filiere_id),
    _normalizedNiveauId: extractId(entity.niveau_id)
  }));
};
// Ajoutez cette fonction
const debugApiStructure = async () => {
  console.log('🔍 Debug de la structure API...');
  
  const testUrls = [
    API_URL,
    `${API_URL}/`,
    `${API_URL}/api`,
    '/api',
    '/api/',
  ];
  
  for (const url of testUrls) {
    try {
      console.log(`📡 Test: ${url}`);
      const response = await axios.get(url, { timeout: 5000 });
      console.log(`✅ Réponse de ${url}:`, response.data);
      console.log('📊 Type:', typeof response.data);
      console.log('🔑 Clés:', Object.keys(response.data));
      
      // Chercher des routes disponibles
      if (typeof response.data === 'object') {
        for (const key in response.data) {
          console.log(`  - ${key}: ${typeof response.data[key]}${Array.isArray(response.data[key]) ? ` (${response.data[key].length} éléments)` : ''}`);
        }
      }
      
      break; // Sortir si on trouve une URL qui fonctionne
    } catch (err) {
      console.log(`❌ Échec ${url}: ${err.message}`);
    }
  }
};

const Etudiants = () => {
  const utilisateur = JSON.parse(localStorage.getItem('user') || '{}');
  // La direction financière consulte la liste en lecture seule.
  const peutModifierEtudiants = ['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CONSULTANT'].includes(utilisateur?.role);
  // États existants
  const [etudiants, setEtudiants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // États pour les dialogues
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingEtudiant, setEditingEtudiant] = useState(null);
  const [deletingEtudiant, setDeletingEtudiant] = useState(null);
  
  // États pour l'affectation
  const [selectedEtudiants, setSelectedEtudiants] = useState([]);
  const [openAffectationDialog, setOpenAffectationDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [affectationLoading, setAffectationLoading] = useState(false);
  
  // États pour l'importation
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState({
    total: 0,
    success: 0,
    errors: 0,
    duplicates: 0,
    details: []
  });
  const [importStep, setImportStep] = useState(0);
  
  // États pour les données de référence (normalisées)
  const [anneesUniversitaires, setAnneesUniversitaires] = useState([]);
  const [allFilieres, setAllFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filiereNiveaux, setFiliereNiveaux] = useState([]);
  
  // États pour les FILTRES (séparés de l'affectation)
  const [filieresFiltrees, setFilieresFiltrees] = useState([]);
  const [niveauxFiltres, setNiveauxFiltres] = useState([]);
  
  // États pour l'AFFECTATION (séparés des filtres)
  const [filieresAffectation, setFilieresAffectation] = useState([]);
  const [niveauxAffectation, setNiveauxAffectation] = useState([]);
  
  const [loadingReferences, setLoadingReferences] = useState(false);
  const [inscriptions, setInscriptions] = useState([]);

  // États pour les filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    annee_universitaire_id: '',
    filiere_id: '',
    niveau_id: '',
    statut_inscription: 'tous',
  });
  const [filteredEtudiants, setFilteredEtudiants] = useState([]);

  // État pour le formulaire d'affectation
  const [affectationData, setAffectationData] = useState({
    annee_universitaire_id: '',
    filiere_id: '',
    niveau_id: '',
    statut: 'CONFIRMEE',
  });
  const [affectationErrors, setAffectationErrors] = useState({});
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Formulaire étudiant
  const [formData, setFormData] = useState({
    CNE: '',
    Nom: '',
    Prénom: '',
    email: '',
    Téléphone: '',
    sexe: '',
    nationalité: 'Maroc',
    année_bac: '',
    date_naissance: '',
    ville_naissance: '',
    pays_naissance: '',
    pourcentage_bourse: 0,
    isActive: true,
    email_père: '',
    téléphone_père: '',
    email_mère: '',
    téléphone_mère: '',
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [documents, setDocuments] = useState({
    photo: { file: null, uploaded: false, download_url: null, file_name: null },
    cin: { file: null, uploaded: false, download_url: null, file_name: null },
    passport: { file: null, uploaded: false, download_url: null, file_name: null },
    cv: { file: null, uploaded: false, download_url: null, file_name: null },
    bac: { file: null, uploaded: false, download_url: null, file_name: null },
    dernier_diplome: { file: null, uploaded: false, download_url: null, file_name: null },
    releves_notes: { file: null, uploaded: false, download_url: null, file_name: null },
  });

  // ============================================================
  // FONCTIONS DE NOTIFICATION
  // ============================================================
  const showNotification = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // ============================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================
  const loadReferences = useCallback(async () => {
  try {
    setLoadingReferences(true);
    setError(null);
    console.log('🔄 Début du chargement des références...');
    console.log('📍 URL API:', API_URL);
    
    // Fonction pour extraire les données quel que soit le format
    const extractData = (response) => {
      if (!response || !response.data) return [];
      
      const data = response.data;
      console.log('📊 Structure des données reçues:', typeof data, Array.isArray(data) ? 'Array' : 'Object');
      
      // Si c'est directement un tableau
      if (Array.isArray(data)) {
        console.log('✅ Format: Tableau direct');
        return data;
      }
      
      // Si c'est un objet avec une propriété data
      if (data.data && Array.isArray(data.data)) {
        console.log('✅ Format: { data: [...] }');
        return data.data;
      }
      
      // Si c'est un objet avec succès et data
      if (data.success && data.data && Array.isArray(data.data)) {
        console.log('✅ Format: { success: true, data: [...] }');
        return data.data;
      }
      
      // Chercher le premier tableau dans l'objet
      for (const key in data) {
        if (Array.isArray(data[key])) {
          console.log(`✅ Format: { ${key}: [...] }`);
          return data[key];
        }
      }
      
      console.warn('⚠️ Format de données inconnu:', data);
      return [];
    };
    
    // Fonction pour normaliser les IDs
    const normalizeIds = (items) => {
      return items.map(item => ({
        ...item,
        _normalizedId: (item._id || item.id || item.annee_id || item.filiere_id || item.niveau_id)?.toString()
      }));
    };
    
    // Tableau pour stocker les résultats
    let anneesList = [];
    let filieresList = [];
    let niveauxList = [];
    let inscriptionsList = [];
    
    // Essayer de charger les données avec différentes méthodes
    const endpoints = [
      { key: 'annees', urls: ['/annees-universitaires', '/annees', '/years', '/academic-years'] },
      { key: 'filieres', urls: ['/filieres', '/filiere', '/programs', '/majors'] },
      { key: 'niveaux', urls: ['/niveaux', '/niveau', '/levels', '/grades'] },
      { key: 'inscriptions', urls: ['/inscriptions', '/inscription', '/enrollments', '/registrations'] },
    ];
    
    // Essayer chaque endpoint
    for (const endpoint of endpoints) {
      let loaded = false;
      
      for (const url of endpoint.urls) {
        if (loaded) break;
        
        try {
          const fullUrl = `${API_URL}${url}`;
          console.log(`🔍 Essai: ${fullUrl}`);
          
          const response = await axios.get(fullUrl, { timeout: 10000 });
          console.log(`✅ Succès: ${fullUrl} (status: ${response.status})`);
          
          const data = extractData(response);
          console.log(`📦 Données extraites pour ${endpoint.key}:`, data.length, 'éléments');
          
          if (data.length > 0) {
            switch (endpoint.key) {
              case 'annees':
                anneesList = normalizeIds(data);
                console.log('📅 Années:', anneesList.map(a => a.intitule || a.nom || a.name).join(', '));
                break;
              case 'filieres':
                filieresList = normalizeIds(data);
                console.log('🏫 Filières:', filieresList.map(f => f.intitule || f.nom || f.name).join(', '));
                break;
              case 'niveaux':
                niveauxList = normalizeIds(data);
                console.log('📚 Niveaux:', niveauxList.map(n => n.intitule || n.nom || n.name).join(', '));
                break;
              case 'inscriptions':
                inscriptionsList = data;
                console.log('📝 Inscriptions:', inscriptionsList.length);
                break;
            }
            loaded = true;
          }
        } catch (err) {
          console.log(`❌ Échec: ${endpoint.key} - ${url} (${err.message})`);
          
          if (err.response) {
            console.log(`   Statut: ${err.response.status}`);
            // Si c'est un 404, on continue avec la prochaine URL
            // Si c'est une autre erreur (500, etc.), on log mais on continue aussi
          }
        }
      }
      
      if (!loaded) {
        console.warn(`⚠️ Impossible de charger ${endpoint.key} avec aucune URL`);
      }
    }
    
    // Si on n'a toujours rien, essayer avec une approche différente
    if (anneesList.length === 0 && filieresList.length === 0 && niveauxList.length === 0) {
      console.log('🔄 Aucune donnée chargée, tentative avec approche alternative...');
      
      // Essayer de récupérer toute la structure de l'API d'un coup
      try {
        console.log('🔍 Tentative de récupération globale...');
        const response = await axios.get(API_URL, { timeout: 10000 });
        console.log('📊 Structure racine de l\'API:', response.data);
        
        // Chercher des tableaux dans la réponse
        const data = response.data;
        for (const key in data) {
          if (Array.isArray(data[key]) && data[key].length > 0) {
            console.log(`✅ Trouvé: ${key} (${data[key].length} éléments)`);
            
            // Essayer d'identifier le type de données
            const firstItem = data[key][0];
            if (firstItem.intitule || firstItem.annee_id) {
              console.log('   → Identifié comme années universitaires');
              anneesList = normalizeIds(data[key]);
            } else if (firstItem.code_filiere || firstItem.filiere_id) {
              console.log('   → Identifié comme filières');
              filieresList = normalizeIds(data[key]);
            } else if (firstItem.niveau_id || firstItem.code) {
              console.log('   → Identifié comme niveaux');
              niveauxList = normalizeIds(data[key]);
            }
          }
        }
      } catch (err) {
        console.error('❌ Échec de la récupération globale:', err.message);
      }
    }
    
    console.log('📊 Résumé du chargement:');
    console.log('  - Années:', anneesList.length);
    console.log('  - Filières:', filieresList.length);
    console.log('  - Niveaux:', niveauxList.length);
    console.log('  - Inscriptions:', inscriptionsList.length);
    
    // Mise à jour des états
    setAnneesUniversitaires(anneesList);
    setAllFilieres(filieresList);
    setNiveaux(niveauxList);
    setInscriptions(inscriptionsList);
    
    // Vérifier si on a au moins les données essentielles
    if (anneesList.length === 0 && filieresList.length === 0 && niveauxList.length === 0) {
      throw new Error('Aucune donnée chargée. Vérifiez la structure de l\'API.');
    }
    
    // Si on a seulement les filières mais pas les années, on peut quand même fonctionner
    if (filieresList.length > 0 && anneesList.length === 0) {
      console.log('⚠️ Seulement les filières sont chargées, création d\'années virtuelles');
      
      // Créer des années virtuelles basées sur les filières
      const anneesVirtuelles = [...new Set(filieresList.map(f => f.annee_id).filter(Boolean))].map(id => ({
        _id: id,
        annee_id: id,
        intitule: `Année ${id}`,
        estActive: false
      }));
      
      if (anneesVirtuelles.length > 0) {
        anneesList = normalizeIds(anneesVirtuelles);
        setAnneesUniversitaires(anneesList);
        console.log('📅 Années virtuelles créées:', anneesList.length);
      }
    }
    
    // Sélectionner une année par défaut
    if (anneesList.length > 0) {
      const anneeActive = anneesList.find(a => a.estActive === true) || anneesList[0];
      if (anneeActive) {
        const anneeId = (anneeActive.annee_id || anneeActive._id || anneeActive._normalizedId)?.toString();
        console.log('⭐ Année sélectionnée:', anneeActive.intitule, 'ID:', anneeId);
        
        setAffectationData(prev => ({
          ...prev,
          annee_universitaire_id: anneeId || ''
        }));
        
        // Filtrer les filières pour l'année sélectionnée
        if (filieresList.length > 0 && anneeId) {
          const filieresParAnnee = filieresList.filter(f => {
            const fAnneeId = (f.annee_id || '').toString();
            return fAnneeId === anneeId || 
                   fAnneeId === anneeActive._id?.toString() ||
                   fAnneeId === anneeActive.annee_id?.toString();
          });
          
          console.log('🏫 Filières pour cette année:', filieresParAnnee.length);
          setFilieresAffectation(filieresParAnnee.length > 0 ? filieresParAnnee : filieresList);
        } else if (filieresList.length > 0) {
          setFilieresAffectation(filieresList);
        }
      }
    } else if (filieresList.length > 0) {
      // Si pas d'années mais des filières, on peut les utiliser directement
      setFilieresAffectation(filieresList);
    }
    
    console.log('✅ Chargement des références terminé');
    
    if (anneesList.length > 0 || filieresList.length > 0) {
      showNotification('Données chargées avec succès', 'success');
    } else {
      showNotification('Données chargées partiellement', 'warning');
    }
    
  } catch (err) {
    console.error('❌ Erreur lors du chargement des références:');
    console.error('Message:', err.message);
    
    if (err.response) {
      console.error('Statut HTTP:', err.response.status);
      console.error('Données:', err.response.data);
      console.error('Headers:', err.response.headers);
    }
    
    setError(`Erreur: ${err.message}`);
    showNotification(`Erreur: ${err.message}`, 'error');
  } finally {
    setLoadingReferences(false);
  }
}, []);
  // ============================================================
  // loadNiveauxByFiliere - retourne les niveaux (pour filtres et affectation)
  // ============================================================
  const loadNiveauxByFiliere = useCallback(async (filiereId) => {
    if (!filiereId) return [];

    try {
      setLoadingReferences(true);
      const filiere = allFilieres.find(f => 
        compareIds(f._id, filiereId) || 
        compareIds(f.filiere_id, filiereId) ||
        compareIds(f._normalizedId, filiereId)
      );

      // Source complete : la collection `filiereNiveaux` via `GET /api/filieres/:id/niveaux`,
      // c est la reference (82 liens). `filieres[].niveaux` est incomplet pour 2026/2027 :
      // la filiere 87 (MASI) ne reference que [21,33,34] alors que 48 (L3 EAF) et 49
      // (L3 Logistique) y sont relies, et une dizaine de filieres ont `niveaux: []`.
      // Sans cet appel, L3 EAF, L3 Logistique, 3GI, Genie Civil, Mecatronique, etc.
      // disparaissaient du filtre « Niveau » et de l export.
      const apiFiliereId = extractId(filiere?._id || filiere?.filiere_id || filiereId);
      let apiList = [];
      try {
        const response = await axios.get(`${API_URL}/filieres/${apiFiliereId}/niveaux`);
        apiList = Array.isArray(response.data?.data || response.data) ? (response.data?.data || response.data) : [];
      } catch (e) {
        console.warn('⚠️ Erreur chargement niveaux (API):', e.message);
      }

      // On dedoublonne l appel API et filiere.niveaux (meme fichier niveau peut apparaitre deux fois),
      // puis on retient les vraies fiches niveaux pour l affichage du filtre et de l affectation.
      const ids = new Set((apiList.length > 0 ? apiList : (filiere?.niveaux || []))
        .map(n => (typeof n === 'object' ? (n.niveau_id || n._id) : n))
        .filter(Boolean)
        .map(x => extractId(x)));

      const matched = niveaux.filter(n => ids.has(extractId(n.niveau_id)) || ids.has(String(n._id)));
      if (matched.length > 0) {
        return matched.sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0));
      }
      if (apiList.length > 0) return normalizeIds(apiList);
      if (filiere?.niveaux?.length) {
        const first = filiere.niveaux[0];
        if (typeof first === 'object' && (first.intitule || first.nom || first.code)) {
          return normalizeIds(filiere.niveaux);
        }
      }
      return [];
    } catch (err) {
      console.warn('⚠️ Erreur chargement niveaux:', err.message);
      return [];
    } finally {
      setLoadingReferences(false);
    }
  }, [allFilieres, niveaux]);

  const loadEtudiants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      
      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm;
        params.page = page + 1;
        params.limit = rowsPerPage;
      } else {
        // Charger tous les étudiants pour permettre le filtrage client
        params.limit = 100000;
        params.page = 1;
      }
      
      const response = await axios.get(`${API_URL}/etudiants`, { params });
      
      const data = response.data;
      let etudiantsList = [];
      if (Array.isArray(data)) {
        etudiantsList = data;
        setTotalCount(data.length);
      } else if (data?.data && Array.isArray(data.data)) {
        etudiantsList = data.data;
        setTotalCount(data.total || data.data.length);
      } else {
        etudiantsList = [];
        setTotalCount(0);
      }
      
      setEtudiants(etudiantsList);
      setFilteredEtudiants(etudiantsList);
      setSelectedEtudiants([]);
      
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors du chargement';
      setError(message);
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm]);

  // ============================================================
  // APPLICATION DES FILTRES
  // ============================================================
  const applyFilters = useCallback(() => {
    console.log('🔍 Application des filtres:', filters);
    let result = [...etudiants];

    // Filtre par année universitaire (ignoré si on cherche les non-inscrits)
    if (filters.annee_universitaire_id && filters.statut_inscription !== 'non_inscrit') {
      const annee = findById(anneesUniversitaires, filters.annee_universitaire_id);
      if (annee) {
        const allowedAnneeIds = [annee._id, annee.annee_id, filters.annee_universitaire_id]
          .map(extractId)
          .filter(Boolean);
        console.log('🔍 Filtrage par année:', annee.intitule, 'IDs:', allowedAnneeIds);
        
        result = result.filter(etudiant => {
          const etudiantId = extractId(etudiant._id || etudiant.id);
          const etudiantInscriptions = inscriptions.filter(ins => {
            const insEtudiantId = extractId(ins.etudiant_id);
            const insAnneeId = extractId(ins.annee_universitaire_id);
            return insEtudiantId === etudiantId && allowedAnneeIds.some(id => compareIds(insAnneeId, id));
          });
          return etudiantInscriptions.length > 0;
        });
        console.log('✅ Après filtre année:', result.length, 'étudiants');
      }
    }

    // Filtre par filière (ignoré si on cherche les non-inscrits)
    if (filters.filiere_id && filters.statut_inscription !== 'non_inscrit') {
      const filiere = findById(allFilieres, filters.filiere_id);
      if (filiere) {
        const allowedFiliereIds = [filiere._id, filiere.filiere_id, filters.filiere_id]
          .map(extractId)
          .filter(Boolean);
        console.log('🔍 Filtrage par filière:', filiere.intitule, 'IDs:', allowedFiliereIds);
        
        result = result.filter(etudiant => {
          const etudiantId = extractId(etudiant._id || etudiant.id);
          const etudiantInscriptions = inscriptions.filter(ins => {
            const insEtudiantId = extractId(ins.etudiant_id);
            const insFiliereId = extractId(ins.filiere_id);
            return insEtudiantId === etudiantId && allowedFiliereIds.some(id => compareIds(insFiliereId, id));
          });
          return etudiantInscriptions.length > 0;
        });
        console.log('✅ Après filtre filière:', result.length, 'étudiants');
      }
    }

    // Filtre par niveau (ignoré si on cherche les non-inscrits)
    if (filters.niveau_id && filters.statut_inscription !== 'non_inscrit') {
      const niveau = niveauxFiltres.find(n => 
        compareIds(n._id, filters.niveau_id) || 
        compareIds(n.niveau_id, filters.niveau_id) ||
        compareIds(n._normalizedId, filters.niveau_id)
      );
      if (niveau) {
        const allowedNiveauIds = [niveau._id, niveau.niveau_id, filters.niveau_id]
          .map(extractId)
          .filter(Boolean);
        console.log('🔍 Filtrage par niveau:', niveau.intitule, 'IDs:', allowedNiveauIds);
        
        result = result.filter(etudiant => {
          const etudiantId = extractId(etudiant._id || etudiant.id);
          const etudiantInscriptions = inscriptions.filter(ins => {
            const insEtudiantId = extractId(ins.etudiant_id);
            const insNiveauId = extractId(ins.niveau_id);
            return insEtudiantId === etudiantId && allowedNiveauIds.some(id => compareIds(insNiveauId, id));
          });
          return etudiantInscriptions.length > 0;
        });
        console.log('✅ Après filtre niveau:', result.length, 'étudiants');
      }
    }

    // Filtre par statut d'inscription
    if (filters.statut_inscription !== 'tous') {
      console.log('🔍 Filtrage par statut:', filters.statut_inscription);
      
      result = result.filter(etudiant => {
        const etudiantId = (etudiant._id || etudiant.id)?.toString();
        const etudiantInscriptions = inscriptions.filter(ins => 
          (ins.etudiant_id?._id || ins.etudiant_id)?.toString() === etudiantId
        );
        
        const hasActiveInscription = etudiantInscriptions.some(ins => ins.statut !== 'ANNULEE');
        const hasConfirmedInscription = etudiantInscriptions.some(ins => ins.statut === 'CONFIRMEE');
        const hasPendingInscription = etudiantInscriptions.some(ins => ins.statut === 'EN_ATTENTE');

        if (filters.statut_inscription === 'inscrit') {
          return hasConfirmedInscription;
        } else if (filters.statut_inscription === 'en_attente') {
          return hasPendingInscription && !hasConfirmedInscription;
        } else if (filters.statut_inscription === 'non_inscrit') {
          return !hasActiveInscription;
        }
        return true;
      });
      console.log('✅ Après filtre statut:', result.length, 'étudiants');
    }

    setFilteredEtudiants(result);
    setTotalCount(result.length);
  }, [etudiants, filters, anneesUniversitaires, allFilieres, niveauxFiltres, inscriptions]);

  // Réinitialiser la page uniquement quand les critères de filtre changent
  useEffect(() => {
    setPage(0);
  }, [filters]);

  // ============================================================
  // EFFETS
  // ============================================================
  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  useEffect(() => {
    loadEtudiants();
  }, [loadEtudiants]);

  useEffect(() => {
    if (etudiants.length > 0) {
      applyFilters();
    }
  }, [applyFilters, etudiants]);

  // ============================================================
  // EFFETS POUR LES FILTRES (cascade) - CORRIGÉS
  // ============================================================
  
  useEffect(() => {
    const anneeId = extractId(filters.annee_universitaire_id);

    if (!anneeId) {
      setFilieresFiltrees([]);
      setNiveauxFiltres([]);
      return;
    }

    const annee = findById(anneesUniversitaires, filters.annee_universitaire_id);
    if (!annee) {
      setFilieresFiltrees([]);
      return;
    }

    const allowedAnneeIds = [annee._id, annee.annee_id, filters.annee_universitaire_id]
      .map(extractId)
      .filter(Boolean);

    const filieres = allFilieres.filter(f =>
      allowedAnneeIds.some(id => compareIds(f.annee_id, id))
    );
    setFilieresFiltrees(filieres);

    setFilters(prev => {
      if (!prev.filiere_id) return prev;
      const stillValid = filieres.some(f =>
        compareIds(f._id, prev.filiere_id) ||
        compareIds(f.filiere_id, prev.filiere_id) ||
        compareIds(f._normalizedId, prev.filiere_id)
      );
      if (!stillValid) {
        setNiveauxFiltres([]);
        return { ...prev, filiere_id: '', niveau_id: '' };
      }
      return prev;
    });
  }, [filters.annee_universitaire_id, anneesUniversitaires, allFilieres]);

  useEffect(() => {
    const filiereId = extractId(filters.filiere_id);
    let cancelled = false;

    if (filiereId) {
      loadNiveauxByFiliere(filiereId).then(niveauxList => {
        if (cancelled) return;
        setNiveauxFiltres(niveauxList);
        setFilters(prev => {
          if (!prev.niveau_id) return prev;
          const stillValid = niveauxList.some(n =>
            compareIds(n._id, prev.niveau_id) ||
            compareIds(n.niveau_id, prev.niveau_id) ||
            compareIds(n._normalizedId, prev.niveau_id)
          );
          return stillValid ? prev : { ...prev, niveau_id: '' };
        });
      });
    } else {
      setNiveauxFiltres([]);
    }

    return () => { cancelled = true; };
  }, [filters.filiere_id, loadNiveauxByFiliere]);

  // ============================================================
  // EFFETS POUR L'AFFECTATION (cascade)
  // ============================================================
  
  useEffect(() => {
    const anneeId = extractId(affectationData.annee_universitaire_id);

    if (!anneeId) {
      setFilieresAffectation([]);
      setNiveauxAffectation([]);
      return;
    }

    const annee = findById(anneesUniversitaires, affectationData.annee_universitaire_id);
    if (!annee) {
      setFilieresAffectation([]);
      return;
    }

    const allowedAnneeIds = [annee._id, annee.annee_id, affectationData.annee_universitaire_id]
      .map(extractId)
      .filter(Boolean);

    const filieres = allFilieres.filter(f =>
      allowedAnneeIds.some(id => compareIds(f.annee_id, id))
    );
    setFilieresAffectation(filieres);

    setAffectationData(prev => {
      if (!prev.filiere_id) return prev;
      const stillValid = filieres.some(f =>
        compareIds(f._id, prev.filiere_id) ||
        compareIds(f.filiere_id, prev.filiere_id) ||
        compareIds(f._normalizedId, prev.filiere_id)
      );
      if (!stillValid) {
        setNiveauxAffectation([]);
        return { ...prev, filiere_id: '', niveau_id: '' };
      }
      return prev;
    });
  }, [affectationData.annee_universitaire_id, anneesUniversitaires, allFilieres]);

  useEffect(() => {
    const filiereId = extractId(affectationData.filiere_id);
    let cancelled = false;

    if (filiereId) {
      loadNiveauxByFiliere(filiereId).then(niveauxList => {
        if (cancelled) return;
        setNiveauxAffectation(niveauxList);
        setAffectationData(prev => {
          if (!prev.niveau_id) return prev;
          const stillValid = niveauxList.some(n =>
            compareIds(n._id, prev.niveau_id) ||
            compareIds(n.niveau_id, prev.niveau_id) ||
            compareIds(n._normalizedId, prev.niveau_id)
          );
          return stillValid ? prev : { ...prev, niveau_id: '' };
        });
      });
    } else {
      setNiveauxAffectation([]);
    }

    return () => { cancelled = true; };
  }, [affectationData.filiere_id, loadNiveauxByFiliere]);

  // ============================================================
  // GESTION DES FILTRES
  // ============================================================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    console.log('🔄 Changement filtre:', name, '→', value);
    
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    console.log('🧹 Nettoyage des filtres');
    setFilters({
      annee_universitaire_id: '',
      filiere_id: '',
      niveau_id: '',
      statut_inscription: 'tous',
    });
    setFilieresFiltrees([]);
    setNiveauxFiltres([]);
    setFilteredEtudiants(etudiants);
    setTotalCount(etudiants.length);
    setPage(0);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // ============================================================
  // GESTION DE LA RECHERCHE
  // ============================================================
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => setPage(0), 300);
    setSearchTimeout(timeout);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ============================================================
  // GESTION DE LA SÉLECTION POUR AFFECTATION
  // ============================================================
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const currentPageIds = filteredEtudiants
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(e => (e._id || e.id)?.toString())
        .filter(Boolean);
      setSelectedEtudiants(currentPageIds);
    } else {
      setSelectedEtudiants([]);
    }
  };

  const handleSelectOne = (event, etudiantId) => {
    const id = etudiantId?.toString();
    if (event.target.checked) {
      setSelectedEtudiants(prev => [...prev, id]);
    } else {
      setSelectedEtudiants(prev => prev.filter(eid => eid !== id));
    }
  };

  const isSelected = (etudiantId) => {
    return selectedEtudiants.indexOf(etudiantId?.toString()) !== -1;
  };

  // ============================================================
  // GESTION DE L'AFFECTATION
  // ============================================================
  const handleOpenAffectationDialog = () => {
    if (selectedEtudiants.length === 0) {
      showNotification('Veuillez sélectionner au moins un étudiant', 'warning');
      return;
    }
    setActiveStep(0);
    setAffectationErrors({});
    setOpenAffectationDialog(true);
  };

  const handleCloseAffectationDialog = () => {
    setOpenAffectationDialog(false);
    setActiveStep(0);
    setAffectationErrors({});
    setAffectationLoading(false);
  };

  const handleAffectationChange = (e) => {
    const { name, value } = e.target;
    console.log('🔄 [AFFECTATION] Changement:', name, '→', value);
    setAffectationData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (affectationErrors[name]) {
      setAffectationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateAffectation = () => {
    const errors = {};
    if (!affectationData.annee_universitaire_id) {
      errors.annee_universitaire_id = 'L\'année universitaire est requise';
    }
    if (!affectationData.filiere_id) {
      errors.filiere_id = 'La filière est requise';
    }
    if (!affectationData.niveau_id) {
      errors.niveau_id = 'Le niveau est requis';
    }
    return errors;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      const errors = validateAffectation();
      if (Object.keys(errors).length > 0) {
        setAffectationErrors(errors);
        showNotification('Veuillez remplir tous les champs requis', 'error');
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleAffecter = async () => {
    try {
      setAffectationLoading(true);
      setLoading(true);

      const anneeObj = findById(anneesUniversitaires, affectationData.annee_universitaire_id);
      const filiereObj = findById(allFilieres, affectationData.filiere_id);
      const niveauObj = findById(niveauxAffectation, affectationData.niveau_id);

      if (!anneeObj || !filiereObj || !niveauObj) {
        showNotification('Erreur: Données de référence non trouvées', 'error');
        setAffectationLoading(false);
        setLoading(false);
        return;
      }

      const mongoAnneeId = anneeObj._id?.toString();
      const mongoFiliereId = filiereObj._id?.toString();
      const mongoNiveauId = niveauObj._id?.toString();

      console.log('📋 Affectation avec:', {
        annee: mongoAnneeId,
        filiere: mongoFiliereId,
        niveau: mongoNiveauId
      });

      // Vérifier les inscriptions existantes
      const checkResults = [];
      for (const etudiantId of selectedEtudiants) {
        try {
          const response = await axios.get(`${API_URL}/inscriptions/etudiant/${etudiantId}`);
          const etudiantInscriptions = response.data.data || response.data || [];
          
          const existingActive = etudiantInscriptions.find(
            ins => ins.statut !== 'ANNULEE' && 
            compareIds(ins.annee_universitaire_id?._id || ins.annee_universitaire_id, mongoAnneeId)
          );
          
          if (existingActive) {
            const etudiant = etudiants.find(e => compareIds(e._id || e.id, etudiantId));
            checkResults.push({
              etudiantId,
              etudiantName: etudiant ? `${etudiant.Nom} ${etudiant.Prénom}` : etudiantId,
              error: `Déjà inscrit (statut: ${existingActive.statut})`
            });
          }
        } catch (err) {
          console.warn('⚠️ Erreur vérification pour', etudiantId, err.message);
        }
      }

      if (checkResults.length > 0) {
        const errorMessages = checkResults.map(r => `- ${r.etudiantName}: ${r.error}`).join('\n');
        showNotification(`Impossible d'affecter certains étudiants:\n${errorMessages}`, 'error');
        setAffectationLoading(false);
        setLoading(false);
        return;
      }

      // Créer les inscriptions
      const promises = selectedEtudiants.map(etudiantId => {
        const inscriptionData = {
          etudiant_id: etudiantId,
          annee_universitaire_id: mongoAnneeId,
          filiere_id: mongoFiliereId,
          niveau_id: mongoNiveauId,
          statut: affectationData.statut || 'CONFIRMEE',
        };
        return axios.post(`${API_URL}/inscriptions`, inscriptionData);
      });

      const results = await Promise.allSettled(promises);
      
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      const errors = [];
      results.forEach((r, index) => {
        if (r.status === 'rejected') {
          const errorMsg = r.reason?.response?.data?.message || r.reason?.message || 'Erreur inconnue';
          errors.push(`Étudiant ${index + 1}: ${errorMsg}`);
        }
      });

      if (failed > 0) {
        showNotification(
          `${succeeded} étudiant(s) affecté(s), ${failed} erreur(s).\n${errors.join('\n')}`,
          failed > 0 && succeeded > 0 ? 'warning' : 'error'
        );
      } else {
        showNotification(`${succeeded} étudiant(s) affecté(s) avec succès`, 'success');
        // Recharger les inscriptions
        const inscriptionsRes = await axios.get(`${API_URL}/inscriptions`);
        setInscriptions(inscriptionsRes.data.data || inscriptionsRes.data || []);
      }

      handleCloseAffectationDialog();
      setSelectedEtudiants([]);
      loadEtudiants();

    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Erreur lors de l\'affectation';
      console.error('❌ Erreur affectation:', err);
      showNotification(message, 'error');
    } finally {
      setAffectationLoading(false);
      setLoading(false);
    }
  };

  // ============================================================
  // GESTION DU FORMULAIRE ÉTUDIANT
  // ============================================================
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.CNE || formData.CNE.trim() === '') {
      errors.CNE = 'Le CNE est requis';
    }
    if (!formData.Nom || formData.Nom.trim() === '') {
      errors.Nom = 'Le nom est requis';
    }
    if (!formData.Prénom || formData.Prénom.trim() === '') {
      errors.Prénom = 'Le prénom est requis';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email invalide';
    }
    return errors;
  };

  // ============================================================
  // CRUD ÉTUDIANT
  // ============================================================
  const handleOpenDialog = (etudiant = null) => {
    if (etudiant) {
      setEditingEtudiant(etudiant);
      setFormData({
        CNE: etudiant.CNE || '',
        Nom: etudiant.Nom || '',
        Prénom: etudiant.Prénom || '',
        email: etudiant.email || '',
        Téléphone: etudiant.Téléphone || '',
        sexe: etudiant.sexe || '',
        nationalité: etudiant.nationalité || 'Maroc',
        année_bac: etudiant.année_bac || '',
        date_naissance: etudiant.date_naissance || '',
        ville_naissance: etudiant.ville_naissance || '',
        pays_naissance: etudiant.pays_naissance || '',
        pourcentage_bourse: etudiant.pourcentage_bourse || 0,
        isActive: etudiant.isActive !== undefined ? etudiant.isActive : true,
        email_père: etudiant.email_père || '',
        téléphone_père: etudiant.téléphone_père || '',
        email_mère: etudiant.email_mère || '',
        téléphone_mère: etudiant.téléphone_mère || '',
      });
      if (etudiant.documents) {
        const docs = {};
        Object.entries(etudiant.documents).forEach(([key, val]) => {
          docs[key] = { file: null, uploaded: val?.uploaded || false, download_url: val?.download_url || null, file_name: val?.file_name || null };
        });
        setDocuments(prev => ({ ...prev, ...docs }));
      }
    } else {
      setEditingEtudiant(null);
      setFormData({
        CNE: '',
        Nom: '',
        Prénom: '',
        email: '',
        Téléphone: '',
        sexe: '',
        nationalité: 'Maroc',
        année_bac: '',
        date_naissance: '',
        ville_naissance: '',
        pays_naissance: '',
        pourcentage_bourse: 0,
        isActive: true,
        email_père: '',
        téléphone_père: '',
        email_mère: '',
        téléphone_mère: '',
      });
      setDocuments({
        photo: { file: null, uploaded: false, download_url: null, file_name: null },
        cin: { file: null, uploaded: false, download_url: null, file_name: null },
        passport: { file: null, uploaded: false, download_url: null, file_name: null },
        cv: { file: null, uploaded: false, download_url: null, file_name: null },
        bac: { file: null, uploaded: false, download_url: null, file_name: null },
        dernier_diplome: { file: null, uploaded: false, download_url: null, file_name: null },
        releves_notes: { file: null, uploaded: false, download_url: null, file_name: null },
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEtudiant(null);
    setFormErrors({});
    setDocuments({
      photo: { file: null, uploaded: false, download_url: null, file_name: null },
      cin: { file: null, uploaded: false, download_url: null, file_name: null },
      passport: { file: null, uploaded: false, download_url: null, file_name: null },
      cv: { file: null, uploaded: false, download_url: null, file_name: null },
      bac: { file: null, uploaded: false, download_url: null, file_name: null },
      dernier_diplome: { file: null, uploaded: false, download_url: null, file_name: null },
      releves_notes: { file: null, uploaded: false, download_url: null, file_name: null },
    });
  };

  const uploadDocument = async (etudiantId, docType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    await axios.post(`${API_URL}/etudiants/upload/${docType}/${etudiantId}`, formData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showNotification('Veuillez corriger les erreurs', 'error');
      return;
    }
    
    try {
      setLoading(true);
      let etudiantId;
      
      if (editingEtudiant) {
        await axios.put(`${API_URL}/etudiants/${editingEtudiant._id}`, formData);
        etudiantId = editingEtudiant._id;
        showNotification('Étudiant mis à jour avec succès', 'success');
      } else {
        const res = await axios.post(`${API_URL}/etudiants`, formData);
        etudiantId = res.data.data?._id || res.data._id;
        showNotification('Étudiant créé avec succès', 'success');
      }
      
      const pendingUploads = Object.entries(documents).filter(([, v]) => v.file);
      for (const [docType, { file }] of pendingUploads) {
        try {
          await uploadDocument(etudiantId, docType, file);
        } catch (err) {
          console.error(`Erreur upload ${docType}:`, err);
        }
      }
      
      handleCloseDialog();
      loadEtudiants();
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors de la sauvegarde';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentChange = (docType, file) => {
    setDocuments(prev => ({ ...prev, [docType]: { ...prev[docType], file } }));
  };

  const handleDeleteDocument = async (docType) => {
    if (!editingEtudiant) return;
    try {
      await axios.delete(`${API_URL}/etudiants/upload/${docType}/${editingEtudiant._id}`);
      setDocuments(prev => ({ ...prev, [docType]: { file: null, uploaded: false, download_url: null, file_name: null } }));
      showNotification('Document supprimé', 'success');
    } catch (err) {
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const handleOpenDeleteDialog = (etudiant) => {
    setDeletingEtudiant(etudiant);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingEtudiant(null);
  };

  const handleDelete = async () => {
    if (!deletingEtudiant) return;
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/etudiants/${deletingEtudiant._id}`);
      showNotification('Étudiant supprimé avec succès', 'success');
      handleCloseDeleteDialog();
      loadEtudiants();
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors de la suppression';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // EXPORT DES ÉTUDIANTS
  // ============================================================
  // getExportInscription - retourne l'inscription a afficher dans l'export.
  // On privilegie l'inscription qui correspond AUX FILTRES courants (annee,
  // filiere, niveau) : sans cela, un etudiant inscrit en L3 EAF en 2026/2027
  // apres une annee en L2 MASI 2025/2026 etait affiche avec l'ancienne
  // inscription (niveau de l'annee precedente) si celle-ci apparaissait
  // avant dans la liste.
  const getExportInscription = (etudiantId) => {
    const id = etudiantId?.toString();
    const candidats = inscriptions.filter(ins =>
      compareIds(ins.etudiant_id?._id || ins.etudiant_id, id) &&
      (ins.statut === 'CONFIRMEE' || ins.statut === 'EN_ATTENTE')
    );
    if (candidats.length === 0) return null;

    const f = extractId(filters.filiere_id);
    const n = extractId(filters.niveau_id);
    const a = extractId(filters.annee_universitaire_id);

    if (!a && !f && !n) return candidats[0];

    // Memes regles de resolution d identifiants que applyFilters (les
    // inscriptions stockent des ObjectId, le filtre porte des codes metier).
    const anneeDoc = findById(anneesUniversitaires, a);
    const filiereDoc = findById(allFilieres, f);
    const niveauDoc = niveauxFiltres.find(nv =>
      compareIds(nv._id, n) || compareIds(nv.niveau_id, n) || compareIds(nv._normalizedId, n)
    );
    const allowedAnneeIds = anneeDoc ? [anneeDoc._id, anneeDoc.annee_id, a].map(extractId).filter(Boolean) : [];
    const allowedFiliereIds = filiereDoc ? [filiereDoc._id, filiereDoc.filiere_id, f].map(extractId).filter(Boolean) : [];
    const allowedNivIds = niveauDoc ? [niveauDoc._id, niveauDoc.niveau_id, n].map(extractId).filter(Boolean) : [];

    const correspond = candidats.find(ins =>
      (!a || allowedAnneeIds.length === 0 || allowedAnneeIds.some(id => compareIds(extractId(ins.annee_universitaire_id), id))) &&
      (!f || allowedFiliereIds.length === 0 || allowedFiliereIds.some(id => compareIds(extractId(ins.filiere_id), id))) &&
      (!n || allowedNivIds.length === 0 || allowedNivIds.some(id => compareIds(extractId(ins.niveau_id), id)))
    );
    return correspond || candidats[0];
  };

  const handleExportExcel = () => {
    const columns = [
      { key: 'cne', label: 'CNE' },
      { key: 'nom', label: 'Nom' },
      { key: 'prenom', label: 'Prénom' },
      { key: 'email', label: 'Email' },
      { key: 'telephone', label: 'Téléphone' },
      { key: 'filiere', label: 'Filière' },
      { key: 'niveau', label: 'Niveau' },
      { key: 'annee', label: 'Année' },
      { key: 'statut', label: 'Statut' }
    ];
    const data = filteredEtudiants.map(e => {
      const ins = getExportInscription(e._id || e.id);
      return {
        cne: e.CNE || '-',
        nom: e.Nom || '-',
        prenom: e.Prénom || '-',
        email: e.email || '-',
        telephone: e.Téléphone || e.telephone || '-',
        filiere: ins?.filiere?.libelle || ins?.filiere?.intitule || '-',
        niveau: ins?.niveau?.intitule || '-',
        annee: ins?.anneeUniversitaire?.intitule || '-',
        statut: ins?.statut === 'CONFIRMEE' ? 'Inscrit' : ins?.statut === 'EN_ATTENTE' ? 'En attente' : 'Non inscrit'
      };
    });
    exportToExcel(data, 'Liste_Etudiants', columns);
  };

  const handleExportPDF = async () => {
    const columns = [
      { key: 'cne', label: 'CNE' },
      { key: 'nom', label: 'Nom' },
      { key: 'prenom', label: 'Prénom' },
      { key: 'email', label: 'Email' },
      { key: 'filiere', label: 'Filière' },
      { key: 'niveau', label: 'Niveau' },
      { key: 'annee', label: 'Année' },
      { key: 'statut', label: 'Statut' }
    ];
    const data = filteredEtudiants.map(e => {
      const ins = getExportInscription(e._id || e.id);
      return {
        cne: e.CNE || '-',
        nom: e.Nom || '-',
        prenom: e.Prénom || '-',
        email: e.email || '-',
        filiere: ins?.filiere?.libelle || ins?.filiere?.intitule || '-',
        niveau: ins?.niveau?.intitule || '-',
        annee: ins?.anneeUniversitaire?.intitule || '-',
        statut: ins?.statut === 'CONFIRMEE' ? 'Inscrit' : ins?.statut === 'EN_ATTENTE' ? 'En attente' : 'Non inscrit'
      };
    });
    await exportToPDF(data, 'Liste_des_Etudiants', columns);
  };

  const normalizeStr = (str) => {
    return (str || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let pwd = '';
    for (let i = 0; i < 8; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    return pwd;
  };

  const handleExportUsersJSON = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const usersRes = await axios.get('/api/users', { headers });
      const existingUsers = usersRes.data || [];
      const existingEmails = new Set(existingUsers.map(u => u.email?.toLowerCase().trim()));

      const emailCount = {};
      const data = filteredEtudiants
        .filter(e => {
          const basePrenom = normalizeStr(e.Prénom);
          const baseNom = normalizeStr(e.Nom);
          const email = `${basePrenom}.${baseNom}@sup2i.ac`;
          return !existingEmails.has(email.toLowerCase().trim());
        })
        .map(e => {
          const basePrenom = normalizeStr(e.Prénom);
          const baseNom = normalizeStr(e.Nom);
          let email = `${basePrenom}.${baseNom}@sup2i.ac`;
          if (emailCount[email]) {
            emailCount[email]++;
            email = `${basePrenom}.${baseNom}${emailCount[email]}@sup2i.ac`;
          } else {
            emailCount[email] = 1;
          }
          return {
            nom: e.Nom || '',
            prenom: e.Prénom || '',
            email,
            password: generatePassword(),
            role: 'ETUDIANT'
          };
        });
      if (data.length === 0) {
        alert('Tous les étudiants ont déjà un compte utilisateur.');
        return;
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'comptes_utilisateurs_sans_compte.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Erreur export JSON:', err);
      alert('Erreur lors de la récupération des utilisateurs existants');
    }
  };

  // ============================================================
  // GESTION DE L'IMPORTATION
  // ============================================================
  const handleOpenImportDialog = () => {
    setOpenImportDialog(true);
    setImportStep(0);
    setImportFile(null);
    setImportData([]);
    setImportPreview([]);
    setImportResults({
      total: 0,
      success: 0,
      errors: 0,
      duplicates: 0,
      details: []
    });
    setImportProgress(0);
  };

  const handleCloseImportDialog = () => {
    setOpenImportDialog(false);
    setImportStep(0);
    setImportFile(null);
    setImportData([]);
    setImportPreview([]);
    setImporting(false);
  };

  const handleFileUpload = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      setImportFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          
          if (!Array.isArray(jsonData)) {
            showNotification('Le fichier doit contenir un tableau d\'étudiants', 'error');
            return;
          }
          
          const validData = jsonData.filter(item => item.CNE && item.Nom && item.Prénom);
          const invalidData = jsonData.filter(item => !item.CNE || !item.Nom || !item.Prénom);
          
          if (invalidData.length > 0) {
            showNotification(`${invalidData.length} entrées ont été ignorées (CNE, Nom ou Prénom manquant)`, 'warning');
          }
          
          setImportData(validData);
          setImportPreview(validData.slice(0, 10));
          setImportStep(1);
        } catch (err) {
          showNotification('Erreur lors de la lecture du fichier JSON: ' + err.message, 'error');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleImport = async () => {
    if (importData.length === 0) {
      showNotification('Aucune donnée à importer', 'warning');
      return;
    }

    setImporting(true);
    setImportProgress(0);
    
    const results = {
      total: importData.length,
      success: 0,
      errors: 0,
      duplicates: 0,
      details: []
    };

    for (let i = 0; i < importData.length; i++) {
      const etudiantData = importData[i];
      try {
        const response = await axios.post(`${API_URL}/etudiants`, etudiantData);
        
        if (response.data.success) {
          results.success++;
          results.details.push({
            CNE: etudiantData.CNE,
            status: 'success',
            message: 'Importé avec succès'
          });
        }
      } catch (err) {
        if (err.response?.data?.message?.includes('existe déjà')) {
          results.duplicates++;
          results.details.push({
            CNE: etudiantData.CNE,
            status: 'duplicate',
            message: err.response?.data?.message || 'Étudiant déjà existant'
          });
        } else {
          results.errors++;
          results.details.push({
            CNE: etudiantData.CNE || 'Inconnu',
            status: 'error',
            message: err.response?.data?.message || err.message || 'Erreur inconnue'
          });
        }
      }
      
      setImportProgress(Math.round(((i + 1) / importData.length) * 100));
    }

    setImportResults(results);
    setImportStep(2);
    setImporting(false);
    
    showNotification(
      `Importation terminée: ${results.success} succès, ${results.errors} erreurs, ${results.duplicates} doublons`,
      results.errors > 0 ? 'warning' : 'success'
    );
    
    loadEtudiants();
  };

  const downloadTemplate = () => {
    const template = [
      {
        "CNE": "CNE12345",
        "Nom": "Dupont",
        "Prénom": "Jean",
        "email": "jean.dupont@email.com",
        "Téléphone": "0612345678",
        "sexe": "M",
        "nationalité": "Maroc",
        "année_bac": "2023/2024",
        "date_naissance": "15/03/2005",
        "ville_naissance": "Casablanca",
        "pays_naissance": "Maroc",
        "pourcentage_bourse": 0,
        "isActive": true,
        "email_père": "pere@email.com",
        "téléphone_père": "0612345679",
        "email_mère": "mere@email.com",
        "téléphone_mère": "0612345680"
      }
    ];
    
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_etudiants.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // RENDU DES STATUTS
  // ============================================================
  const getEtudiantStatus = (etudiantId) => {
    const id = etudiantId?.toString();
    const etudiantInscriptions = inscriptions.filter(ins => 
      compareIds(ins.etudiant_id?._id || ins.etudiant_id, id)
    );
    
    const hasConfirmed = etudiantInscriptions.some(ins => ins.statut === 'CONFIRMEE');
    const hasPending = etudiantInscriptions.some(ins => ins.statut === 'EN_ATTENTE');
    const hasActive = hasConfirmed || hasPending;

    if (hasConfirmed) {
      return { label: 'Inscrit', color: 'success', icon: <CheckCircleIcon /> };
    } else if (hasPending) {
      return { label: 'En attente', color: 'warning', icon: <PendingIcon /> };
    } else {
      return { label: 'Non inscrit', color: 'default', icon: <CancelIcon /> };
    }
  };

  // ============================================================
  // RENDU DES ÉTAPES D'AFFECTATION
  // ============================================================
  const steps = [
    {
      label: 'Choisir l\'année, la filière et le niveau',
      icon: <SchoolIcon />,
      description: 'Sélectionnez en cascade : Année → Filière → Niveau',
    },
    {
      label: 'Confirmer l\'affectation',
      icon: <AssignmentIcon />,
      description: 'Vérifiez les étudiants à affecter',
    },
  ];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Sélectionnez en cascade : Année Universitaire → Filière → Niveau
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!affectationErrors.annee_universitaire_id}>
                  <InputLabel>Année Universitaire *</InputLabel>
                  <Select
                    name="annee_universitaire_id"
                    value={affectationData.annee_universitaire_id || ''}
                    onChange={handleAffectationChange}
                    label="Année Universitaire *"
                    disabled={loadingReferences}
                  >
                    <MenuItem value="">
                      <em>Sélectionner une année</em>
                    </MenuItem>
                    {anneesUniversitaires.map((annee) => {
                      const id = annee.annee_id || annee._id;
                      return (
                        <MenuItem key={id} value={id?.toString()}>
                          {annee.intitule} {annee.estActive && '⭐ (Active)'}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {affectationErrors.annee_universitaire_id && (
                    <Typography variant="caption" color="error">
                      {affectationErrors.annee_universitaire_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth error={!!affectationErrors.filiere_id}>
                  <InputLabel>Filière *</InputLabel>
                  <Select
                    name="filiere_id"
                    value={affectationData.filiere_id || ''}
                    onChange={handleAffectationChange}
                    label="Filière *"
                    disabled={!affectationData.annee_universitaire_id || loadingReferences}
                  >
                    <MenuItem value="">
                      <em>Choisir une filière</em>
                    </MenuItem>
                    {filieresAffectation.map((filiere) => {
                      const id = filiere.filiere_id || filiere._id;
                      return (
                        <MenuItem key={id} value={id?.toString()}>
                          {filiere.code_filiere} - {filiere.intitule}
                          {filiere.libelle && ` (${filiere.libelle})`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {affectationErrors.filiere_id && (
                    <Typography variant="caption" color="error">
                      {affectationErrors.filiere_id}
                    </Typography>
                  )}
                  {!affectationData.annee_universitaire_id && (
                    <Typography variant="caption" color="text.secondary">
                      Sélectionnez d'abord une année universitaire
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth error={!!affectationErrors.niveau_id}>
                  <InputLabel>Niveau *</InputLabel>
                  <Select
                    name="niveau_id"
                    value={affectationData.niveau_id || ''}
                    onChange={handleAffectationChange}
                    label="Niveau *"
                    disabled={!affectationData.filiere_id || loadingReferences}
                  >
                    <MenuItem value="">
                      <em>Choisir un niveau</em>
                    </MenuItem>
                    {Array.isArray(niveauxAffectation) && niveauxAffectation.length > 0 ? (
                      niveauxAffectation.map((niveau) => {
                        const id = niveau.niveau_id || niveau._id;
                        return (
                          <MenuItem key={id} value={id?.toString()}>
                            {niveau.code} - {niveau.intitule}
                            {niveau.description && ` (${niveau.description})`}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem disabled value="">
                        <em>{loadingReferences ? 'Chargement...' : 'Aucun niveau disponible'}</em>
                      </MenuItem>
                    )}
                  </Select>
                  {affectationErrors.niveau_id && (
                    <Typography variant="caption" color="error">
                      {affectationErrors.niveau_id}
                    </Typography>
                  )}
                  {!affectationData.filiere_id && affectationData.annee_universitaire_id && (
                    <Typography variant="caption" color="text.secondary">
                      Sélectionnez d'abord une filière
                    </Typography>
                  )}
                  {affectationData.filiere_id && (!Array.isArray(niveauxAffectation) || niveauxAffectation.length === 0) && !loadingReferences && (
                    <Typography variant="caption" color="warning.main">
                      ⚠️ Aucun niveau disponible pour cette filière
                    </Typography>
                  )}
                  {/* Debug info */}
                  {affectationData.filiere_id && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      Debug: {Array.isArray(niveauxAffectation) ? `${niveauxAffectation.length} niveaux chargés` : 'Aucun niveau'}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="statut"
                    value={affectationData.statut || 'CONFIRMEE'}
                    onChange={handleAffectationChange}
                    label="Statut"
                  >
                    <MenuItem value="CONFIRMEE">Confirmée</MenuItem>
                    <MenuItem value="EN_ATTENTE">En attente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Confirmez l'affectation pour les étudiants sélectionnés
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Chip label="Résumé de l'affectation" size="small" />
                    </Divider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Année Universitaire
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {findById(anneesUniversitaires, affectationData.annee_universitaire_id)?.intitule || ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Filière
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {findById(filieresAffectation, affectationData.filiere_id)?.intitule || ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Niveau
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {findById(niveauxAffectation, affectationData.niveau_id)?.intitule || ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Statut
                    </Typography>
                    <Chip
                      label={affectationData.statut === 'CONFIRMEE' ? 'Confirmée' : 'En attente'}
                      color={affectationData.statut === 'CONFIRMEE' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Étudiants à affecter ({selectedEtudiants.length})
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {selectedEtudiants.map(id => {
                        const etudiant = etudiants.find(e => compareIds(e._id || e.id, id));
                        return etudiant ? (
                          <Box
                            key={id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderBottom: '1px solid #eee',
                            }}
                          >
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                              {etudiant.Prénom?.[0]}{etudiant.Nom?.[0]}
                            </Avatar>
                            <Typography variant="body2">
                              {etudiant.Nom} {etudiant.Prénom}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({etudiant.CNE})
                            </Typography>
                          </Box>
                        ) : null;
                      })}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  // ============================================================
  // RENDU PRINCIPAL
  // ============================================================
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: 3 }}>
        {/* En-tête */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="h5">
                Gestion des Étudiants
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  ({totalCount} étudiants)
                </Typography>
              </Typography>
              {selectedEtudiants.length > 0 && (
                <Chip
                  label={`${selectedEtudiants.length} sélectionné(s)`}
                  color="primary"
                  size="small"
                />
              )}
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={toggleFilters}
              color={showFilters ? 'primary' : 'inherit'}
            >
              Filtres
            </Button>
            {peutModifierEtudiants && (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={handleOpenImportDialog}
              >
                Importer JSON
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportUsersJSON}
            >
              Exporter JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadEtudiants}
              disabled={loading}
            >
              Actualiser
            </Button>
            <ExportButton
              onExportExcel={handleExportExcel}
              onExportPDF={handleExportPDF}
              disabled={filteredEtudiants.length === 0}
            />
            <Button 
  variant="outlined" 
  onClick={debugApiStructure}
  disabled={loadingReferences}
>
  Debug API
</Button>
            {peutModifierEtudiants && (
              <>
                <Button
                  variant="contained"
                  startIcon={<GroupAddIcon />}
                  onClick={handleOpenAffectationDialog}
                  disabled={selectedEtudiants.length === 0 || loading}
                  color="secondary"
                >
                  Affecter ({selectedEtudiants.length})
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Nouvel Étudiant
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Barre de recherche */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Rechercher par CNE, nom, prénom ou email..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Panneau des filtres */}
        <Collapse in={showFilters}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: (theme) => theme.palette.grey[50],
              borderColor: (theme) => theme.palette.primary.main,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterAltIcon color="primary" />
                Filtres avancés
              </Typography>
              <Button 
                size="small" 
                startIcon={<ClearIcon />} 
                onClick={handleClearFilters}
                color="error"
              >
                Effacer les filtres
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Année Universitaire</InputLabel>
                  <Select
                    name="annee_universitaire_id"
                    value={filters.annee_universitaire_id}
                    onChange={handleFilterChange}
                    label="Année Universitaire"
                  >
                    <MenuItem value="">Toutes les années</MenuItem>
                    {anneesUniversitaires.map((annee) => {
                      const id = annee.annee_id || annee._id;
                      return (
                        <MenuItem key={id} value={id?.toString()}>
                          {annee.intitule} {annee.estActive && '⭐'}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filière</InputLabel>
                  <Select
                    name="filiere_id"
                    value={filters.filiere_id}
                    onChange={handleFilterChange}
                    label="Filière"
                    disabled={!filters.annee_universitaire_id}
                  >
                    <MenuItem value="">Toutes les filières</MenuItem>
                    {filieresFiltrees.map((filiere) => {
                      const id = filiere.filiere_id || filiere._id;
                      return (
                        <MenuItem key={id} value={id?.toString()}>
                          {filiere.code_filiere} - {filiere.intitule}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {!filters.annee_universitaire_id && (
                    <Typography variant="caption" color="text.secondary">
                      Sélectionnez une année d'abord
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Niveau</InputLabel>
                  <Select
                    name="niveau_id"
                    value={filters.niveau_id}
                    onChange={handleFilterChange}
                    label="Niveau"
                    disabled={!filters.filiere_id}
                  >
                    <MenuItem value="">Tous les niveaux</MenuItem>
                    {niveauxFiltres.map((niveau) => {
                      const id = niveau.niveau_id || niveau._id;
                      return (
                        <MenuItem key={id} value={id?.toString()}>
                          {niveau.code} - {niveau.intitule}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {!filters.filiere_id && filters.annee_universitaire_id && (
                    <Typography variant="caption" color="text.secondary">
                      Sélectionnez une filière d'abord
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut d'inscription</InputLabel>
                  <Select
                    name="statut_inscription"
                    value={filters.statut_inscription}
                    onChange={handleFilterChange}
                    label="Statut d'inscription"
                  >
                    <MenuItem value="tous">Tous</MenuItem>
                    <MenuItem value="inscrit">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        Inscrit
                      </Box>
                    </MenuItem>
                    <MenuItem value="en_attente">
                      <Box display="flex" alignItems="center" gap={1}>
                        <PendingIcon color="warning" fontSize="small" />
                        En attente
                      </Box>
                    </MenuItem>
                    <MenuItem value="non_inscrit">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CancelIcon color="error" fontSize="small" />
                        Non inscrit
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tableau */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedEtudiants.length > 0 &&
                      selectedEtudiants.length < filteredEtudiants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length
                    }
                    checked={
                      filteredEtudiants.length > 0 &&
                      selectedEtudiants.length === filteredEtudiants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>CNE</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Nationalité</TableCell>
                <TableCell>Statut Inscription</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !filteredEtudiants.length ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredEtudiants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      {searchTerm ? 'Aucun résultat pour cette recherche' : 'Aucun étudiant trouvé'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEtudiants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((etudiant) => {
                  const etudiantId = (etudiant._id || etudiant.id)?.toString();
                  const isItemSelected = isSelected(etudiantId);
                  const status = getEtudiantStatus(etudiantId);
                  return (
                    <TableRow
                      key={etudiantId}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleSelectOne(event, etudiantId)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {etudiant.CNE}
                        </Typography>
                      </TableCell>
                      <TableCell>{etudiant.Nom}</TableCell>
                      <TableCell>{etudiant.Prénom}</TableCell>
                      <TableCell>{etudiant.email || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={etudiant.nationalité || 'N/A'} 
                          size="small"
                          color={etudiant.nationalité === 'Maroc' ? 'default' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={status.icon}
                          label={status.label}
                          color={status.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Attestation de scolarité">
                          <IconButton 
                            size="small" 
                            color="inherit"
                            onClick={() => {
                              const ins = inscriptions.find(i => 
                                compareIds(i.etudiant_id?._id || i.etudiant_id, etudiant._id || etudiant.id) &&
                                (i.statut === 'CONFIRMEE' || i.statut === 'EN_ATTENTE')
                              );
                              generateAttestationPDF(etudiant, ins);
                            }}
                          >
                            <DescriptionIcon />
                          </IconButton>
                        </Tooltip>
                        {peutModifierEtudiants && (
                          <>
                            <Tooltip title="Modifier">
                              <IconButton size="small" color="primary" onClick={() => handleOpenDialog(etudiant)}><EditIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(etudiant)}><DeleteIcon /></IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {/* ============================================================
          DIALOGUE D'IMPORTATION JSON
          ============================================================ */}
      <Dialog 
        open={openImportDialog} 
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CloudUploadIcon color="primary" />
            <Typography variant="h6">
              Importer des étudiants depuis un fichier JSON
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {importStep === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Téléchargez un fichier JSON contenant la liste des étudiants à importer.
                Le fichier doit être un tableau d'objets avec les champs requis.
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<DescriptionIcon />}
                  onClick={downloadTemplate}
                >
                  Télécharger le modèle
                </Button>
              </Box>

              <DropzoneArea
                acceptedFiles={['application/json']}
                filesLimit={1}
                maxFileSize={5242880}
                dropzoneText="Glissez-déposez votre fichier JSON ici ou cliquez pour sélectionner"
                onChange={handleFileUpload}
                showPreviews={false}
                showPreviewsInDropzone={false}
              />

              {importFile && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Fichier chargé: {importFile.name} ({Math.round(importFile.size / 1024)} KB)
                </Alert>
              )}
            </Box>
          )}

          {importStep === 1 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Aperçu des données ({importData.length} étudiants)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Voici un aperçu des 10 premiers étudiants à importer.
              </Typography>
              
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CNE</TableCell>
                      <TableCell>Nom</TableCell>
                      <TableCell>Prénom</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.CNE}</TableCell>
                        <TableCell>{item.Nom}</TableCell>
                        <TableCell>{item.Prénom}</TableCell>
                        <TableCell>{item.email || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {importData.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="caption" color="text.secondary">
                            ... et {importData.length - 10} autres étudiants
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {importing && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Importation en cours... {importProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={importProgress} />
                </Box>
              )}
            </Box>
          )}

          {importStep === 2 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Résultats de l'importation
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                    <Typography variant="h4" color="primary.contrastText">
                      {importResults.total}
                    </Typography>
                    <Typography variant="caption" color="primary.contrastText">
                      Total
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h4" color="success.contrastText">
                      {importResults.success}
                    </Typography>
                    <Typography variant="caption" color="success.contrastText">
                      Succès
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h4" color="warning.contrastText">
                      {importResults.duplicates}
                    </Typography>
                    <Typography variant="caption" color="warning.contrastText">
                      Doublons
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                    <Typography variant="h4" color="error.contrastText">
                      {importResults.errors}
                    </Typography>
                    <Typography variant="caption" color="error.contrastText">
                      Erreurs
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {importResults.details.length > 0 && (
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>CNE</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResults.details.slice(0, 20).map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell>{detail.CNE}</TableCell>
                          <TableCell>
                            <Chip
                              label={detail.status === 'success' ? 'Succès' : 
                                    detail.status === 'duplicate' ? 'Doublon' : 'Erreur'}
                              color={detail.status === 'success' ? 'success' : 
                                    detail.status === 'duplicate' ? 'warning' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{detail.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {importStep === 1 && !importing && (
            <>
              <Button onClick={() => setImportStep(0)}>Retour</Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleImport}
                disabled={importData.length === 0}
              >
                Importer {importData.length} étudiants
              </Button>
            </>
          )}
          {importStep === 2 && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => {
                handleCloseImportDialog();
                loadEtudiants();
              }}
            >
              Terminer
            </Button>
          )}
          {importStep === 0 && (
            <Button onClick={handleCloseImportDialog}>Annuler</Button>
          )}
          {importing && (
            <Button disabled>
              <CircularProgress size={24} />
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ============================================================
          DIALOGUE D'AFFECTATION
          ============================================================ */}
      <Dialog 
        open={openAffectationDialog} 
        onClose={handleCloseAffectationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AssignmentIcon color="secondary" />
            <Typography variant="h6">
              Affectation des étudiants
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel StepIconComponent={() => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {step.icon}
                    </Box>
                  )}>
                    <Box>
                      <Typography variant="subtitle2">{step.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  </StepLabel>
                  <StepContent>
                    {renderStepContent(index)}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={index === steps.length - 1 ? handleAffecter : handleNext}
                        endIcon={index === steps.length - 1 ? null : <ArrowForwardIcon />}
                        disabled={affectationLoading || loading}
                      >
                        {affectationLoading || loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          index === steps.length - 1 ? 'Affecter' : 'Suivant'
                        )}
                      </Button>
                      {index > 0 && (
                        <Button onClick={handleBack} disabled={affectationLoading || loading}>
                          Retour
                        </Button>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseAffectationDialog} disabled={affectationLoading || loading}>
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================
          DIALOGUE D'AJOUT/MODIFICATION ÉTUDIANT
          ============================================================ */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingEtudiant ? 'Modifier l\'étudiant' : 'Ajouter un étudiant'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="CNE"
                  name="CNE"
                  value={formData.CNE}
                  onChange={handleFormChange}
                  error={!!formErrors.CNE}
                  helperText={formErrors.CNE}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Nom"
                  name="Nom"
                  value={formData.Nom}
                  onChange={handleFormChange}
                  error={!!formErrors.Nom}
                  helperText={formErrors.Nom}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Prénom"
                  name="Prénom"
                  value={formData.Prénom}
                  onChange={handleFormChange}
                  error={!!formErrors.Prénom}
                  helperText={formErrors.Prénom}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  name="Téléphone"
                  value={formData.Téléphone}
                  onChange={handleFormChange}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Sexe</InputLabel>
                  <Select
                    name="sexe"
                    value={formData.sexe}
                    onChange={handleFormChange}
                    label="Sexe"
                  >
                    <MenuItem value="">Non précisé</MenuItem>
                    <MenuItem value="M">Masculin</MenuItem>
                    <MenuItem value="F">Féminin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Nationalité"
                  name="nationalité"
                  value={formData.nationalité}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Année du Bac"
                  name="année_bac"
                  value={formData.année_bac}
                  onChange={handleFormChange}
                  placeholder="2020"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date de naissance"
                  name="date_naissance"
                  type="date"
                  value={formData.date_naissance}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Ville de naissance"
                  name="ville_naissance"
                  value={formData.ville_naissance}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pays de naissance"
                  name="pays_naissance"
                  value={formData.pays_naissance}
                  onChange={handleFormChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email du père"
                  name="email_père"
                  type="email"
                  value={formData.email_père}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Téléphone du père"
                  name="téléphone_père"
                  value={formData.téléphone_père}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email de la mère"
                  name="email_mère"
                  type="email"
                  value={formData.email_mère}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Téléphone de la mère"
                  name="téléphone_mère"
                  value={formData.téléphone_mère}
                  onChange={handleFormChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pourcentage de bourse"
                  name="pourcentage_bourse"
                  type="number"
                  value={formData.pourcentage_bourse}
                  onChange={handleFormChange}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleFormChange}
                    />
                  }
                  label="Actif"
                />
              </Grid>

              {/* ===== DOCUMENTS ===== */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Documents</Typography>
              </Grid>
              {[
                { key: 'photo', label: 'Photo', accept: 'image/*' },
                { key: 'cin', label: 'CIN', accept: '.pdf,.jpg,.jpeg,.png' },
                { key: 'passport', label: 'Passeport', accept: '.pdf,.jpg,.jpeg,.png' },
                { key: 'cv', label: 'CV', accept: '.pdf,.doc,.docx' },
                { key: 'bac', label: 'Bac', accept: '.pdf,.jpg,.jpeg,.png' },
                { key: 'dernier_diplome', label: 'Dernier diplôme', accept: '.pdf,.jpg,.jpeg,.png' },
                { key: 'releves_notes', label: 'Relevés post bac', accept: '.pdf,.jpg,.jpeg,.png' },
              ].map(({ key, label, accept }) => {
                const doc = documents[key] || {};
                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
                      {doc.uploaded && doc.download_url ? (
                        <>
                          <DescriptionIcon color="primary" />
                          <Typography variant="body2" sx={{ flex: 1 }}>{doc.file_name || label}</Typography>
                          <IconButton size="small" component="a" href={`${BASE_URL}${doc.download_url}`} target="_blank" color="primary">
                            <DownloadIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteDocument(key)}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button variant="outlined" size="small" component="label" sx={{ minWidth: 120 }}>
                            {label}
                            <input type="file" hidden accept={accept} onChange={e => handleDocumentChange(key, e.target.files[0])} />
                          </Button>
                          {doc.file && (
                            <Typography variant="caption" sx={{ flex: 1, ml: 1 }}>{doc.file.name}</Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : (editingEtudiant ? 'Modifier' : 'Ajouter')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialogue de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'étudiant{' '}
            <strong>{deletingEtudiant?.Nom} {deletingEtudiant?.Prénom}</strong> ?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Etudiants;


